import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogFooter } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { StatusOptionRows } from "@/components/board/StatusOptionRows";
import {
  cleanOptions,
  hasBlankExistingLabel,
  newOptionId,
  optionsChanged,
} from "@/lib/statusOptions";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { roleOf } from "@/lib/statusRoles";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { ViewBoard } from "@/lib/boardView";
import type { StatusOption, UUID } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  board: ViewBoard;
  boardId: UUID;
}

/**
 * Phase 6 v1 — per-board admin panel to manage the CATEGORY status columns
 * (Bereich / Priorität / Vorgangstyp): add / rename (label only — the id is kept
 * so existing cells keep resolving) / recolor / archive. The action-driven
 * WORKFLOW Status column is intentionally excluded (editing its options would
 * break the Annehmen/Lösen/… transitions). Reorder stays in the existing
 * section-header group-drag for now. All saves are read-modify-write of
 * `config.options` via the existing `PATCH /columns/{id}` — no new backend.
 */
export function TicketSettingsDialog({ open, onOpenChange, board, boardId }: Props) {
  const qc = useQueryClient();

  // Category = status columns WITHOUT a lifecycle role on any option.
  const categoryCols = useMemo(
    () =>
      board.columns.filter(
        (c) => c.type === "status" && !(c.options ?? []).some((o) => roleOf(o) != null),
      ),
    [board.columns],
  );

  // Local draft per column, seeded each time the dialog opens so editing doesn't
  // fight live WS column updates.
  const [drafts, setDrafts] = useState<Record<string, StatusOption[]>>({});
  useEffect(() => {
    if (!open) return;
    const next: Record<string, StatusOption[]> = {};
    for (const c of categoryCols) {
      const raw = (c.raw?.config?.options ?? []) as StatusOption[];
      next[c.id] = raw.map((o) => ({ ...o }));
    }
    setDrafts(next);
    // Reseed only on open — depending on categoryCols would clobber edits when a
    // WS update arrives mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = useMutation({
    mutationFn: async () => {
      // Validate ALL columns first (no partial saves). Clearing an EXISTING
      // option's label to blank would silently drop it below and orphan every
      // cell that stored its id — block instead. (New, never-named rows are
      // fine to drop.)
      for (const c of categoryCols) {
        const draft = drafts[c.id];
        if (!draft) continue;
        const original = (c.raw?.config?.options ?? []) as StatusOption[];
        if (hasBlankExistingLabel(draft, original)) {
          throw new Error(t.ticketSettingsBlankLabel);
        }
      }
      for (const c of categoryCols) {
        const draft = drafts[c.id];
        if (!draft) continue;
        const original = (c.raw?.config?.options ?? []) as StatusOption[];
        const cleaned = cleanOptions(draft);
        if (!optionsChanged(cleaned, original)) continue;
        await api.updateColumn(c.id as UUID, {
          config: { ...(c.raw?.config ?? {}), options: cleaned },
        });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.board(boardId) });
      showToast(t.ticketSettingsSaved, "success");
      onOpenChange(false);
    },
    onError: (e) => showToast(e instanceof Error ? e.message : t.unknownError, "error"),
  });

  function setOpt(colId: string, idx: number, patch: Partial<StatusOption>) {
    setDrafts((prev) => ({
      ...prev,
      [colId]: (prev[colId] ?? []).map((o, i) => (i === idx ? { ...o, ...patch } : o)),
    }));
  }
  function addOpt(colId: string) {
    setDrafts((prev) => ({
      ...prev,
      [colId]: [...(prev[colId] ?? []), { id: newOptionId(), label: "", color: "slate" }],
    }));
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !save.isPending && onOpenChange(o)}
      title={t.ticketSettingsTitle}
      description={t.ticketSettingsDescription}
      width={560}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 22,
          maxHeight: "58vh",
          overflowY: "auto",
          paddingRight: 2,
        }}
      >
        {categoryCols.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--ink-3)" }}>{t.ticketSettingsNoColumns}</div>
        ) : (
          categoryCols.map((c) => {
            const opts = drafts[c.id] ?? [];
            return (
              <div key={c.id}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--ink-1)",
                    marginBottom: 8,
                  }}
                >
                  {c.name}
                </div>
                <StatusOptionRows
                  options={opts}
                  required={Boolean(c.raw?.config?.required)}
                  onChange={(i, patch) => setOpt(c.id, i, patch)}
                  onAdd={() => addOpt(c.id)}
                />
              </div>
            );
          })
        )}
        <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: -6 }}>
          {t.ticketSettingsArchiveHint}
        </div>
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={() => onOpenChange(false)}
          disabled={save.isPending}
        >
          {t.cancel}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={() => save.mutate()}
          disabled={save.isPending}
        >
          {save.isPending ? t.saving : t.save}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
